import type { EntityDetail, TenantMembership } from '@cdf/api-client-react';
import {
  BoxesIcon,
  Building2Icon,
  FileTextIcon,
  KeyRoundIcon,
  NetworkIcon,
  PaletteIcon,
  PowerIcon,
  ScrollTextIcon,
  Settings2Icon,
  ShieldIcon,
  SparklesIcon,
  UserPlusIcon,
  UsersRoundIcon,
  type LucideIcon
} from 'lucide-react';

import { hasTenantPermission } from '@/features/tenancy/permissions';

export type TenantAdministrationPage = {
  to: string;
  label: string;
  description: string;
  keywords: readonly string[];
  icon: LucideIcon;
  permissions: readonly string[];
  /** Pages that stay reachable while the entity is suspended or archived. */
  availableWhenInactive?: boolean;
};

export const entityOverviewPage = 'tenant/entity';
export const entityProfilePage = 'tenant/entity/profile';
export const entityPrivacyPage = 'tenant/entity/privacy';
export const entityBrandingPage = 'tenant/entity/branding';
export const entitySecurityTxtPage = 'tenant/entity/security-txt';
export const entityEntitiesPage = 'tenant/entity/entities';
export const entityLifecyclePage = 'tenant/entity/lifecycle';

export const tenantAdministrationNavigation: readonly TenantAdministrationPage[] = [
  { to: entityOverviewPage, label: 'Overview', description: 'Entity and parent tenant status.', keywords: ['entity', 'status', 'parent'], icon: Building2Icon, permissions: ['tenant.settings.read'], availableWhenInactive: true },
  { to: entityProfilePage, label: 'Profile', description: 'Name, contact details and advanced profile fields.', keywords: ['identity', 'website', 'industry', 'locations', 'emails', 'backup'], icon: FileTextIcon, permissions: ['tenant.settings.read'], availableWhenInactive: true },
  { to: entityPrivacyPage, label: 'Privacy', description: 'Entity privacy and permitted audience overrides.', keywords: ['audience', 'booking', 'desk', 'sharing'], icon: ShieldIcon, permissions: ['tenant.settings.read'] },
  { to: entityBrandingPage, label: 'Branding', description: 'Entity logo, colours and presentation.', keywords: ['logo', 'theme', 'skin', 'colours', 'navigation', 'home', 'layout'], icon: PaletteIcon, permissions: ['tenant.settings.read'] },
  { to: entitySecurityTxtPage, label: 'Security.txt', description: 'Security contacts and vulnerability disclosure.', keywords: ['security', 'vulnerability', 'disclosure', 'contact', 'well-known', 'expires'], icon: ShieldIcon, permissions: ['tenant.settings.read'] },
  { to: entityEntitiesPage, label: 'Entities', description: 'Browse entities you can access.', keywords: ['entities', 'switch', 'granted'], icon: NetworkIcon, permissions: ['tenant.settings.read'] },
  { to: entityLifecyclePage, label: 'Lifecycle', description: 'Suspend or restore this entity with an audited reason.', keywords: ['suspend', 'restore'], icon: PowerIcon, permissions: ['tenant.settings.manage'], availableWhenInactive: true },
  { to: 'tenant/members', label: 'Members', description: 'People with explicit access to this entity.', keywords: ['people', 'grants', 'access'], icon: UsersRoundIcon, permissions: ['tenant.members.read'] },
  { to: 'tenant/invites', label: 'Invitations', description: 'Invite people to this entity.', keywords: ['invites', 'people'], icon: UserPlusIcon, permissions: ['tenant.invites.read'] },
  { to: 'tenant/roles', label: 'Roles', description: 'Review and manage entity roles.', keywords: ['permissions', 'access'], icon: KeyRoundIcon, permissions: ['tenant.roles.read'] },
  { to: 'tenant/modules', label: 'Modules', description: 'Feature controls for this entity and its parent.', keywords: ['features', 'controls'], icon: BoxesIcon, permissions: ['tenant.modules.manage'] },
  { to: 'tenant/exchange', label: 'Exchange', description: 'Parent connection and reviewed entity room linking.', keywords: ['calendar', 'rooms', 'outlook'], icon: Settings2Icon, permissions: ['tenant.modules.manage'] },
  { to: 'tenant/settings', label: 'Settings', description: 'Structured entity configuration.', keywords: ['configuration', 'json'], icon: Settings2Icon, permissions: ['tenant.settings.read'] },
  { to: 'tenant/audit', label: 'Audit history', description: 'Security and administration activity.', keywords: ['events', 'history'], icon: ScrollTextIcon, permissions: ['tenant.audit.read'] },
  { to: 'tenant/elevation', label: 'Access elevation', description: 'Request or approve temporary access.', keywords: ['temporary', 'approval'], icon: SparklesIcon, permissions: ['tenant.elevation.request', 'tenant.elevation.approve'] }
];

/** Entity pages whose usefulness depends on the loaded entity and parent status. */
const statusDependentEntityPages: readonly string[] = [
  entityProfilePage,
  entityPrivacyPage,
  entityBrandingPage,
  entitySecurityTxtPage,
  entityEntitiesPage,
  entityLifecyclePage
];

export function canOpenTenantPage(
  grantedPermissions: readonly string[],
  requiredPermissions: readonly string[]
): boolean {
  return requiredPermissions.some((permission) => hasTenantPermission(grantedPermissions, permission));
}

export function isEntityAdministrationPage(to: string): boolean {
  return to === entityOverviewPage || to.startsWith(`${entityOverviewPage}/`);
}

/** True when the administration path may be opened for a suspended or archived entity. */
export function isInactiveEntityAdministrationPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  return tenantAdministrationNavigation.some((item) => item.availableWhenInactive && path.endsWith(`/${item.to}`));
}

export type AdministrationDestination = {
  to: string;
  label: string;
  description: string;
  keywords: readonly string[];
};

export function authorizedAdministrationDestinations(
  permissions: readonly string[],
  options: { active: boolean; availablePages?: readonly string[] }
): AdministrationDestination[] {
  return tenantAdministrationNavigation
    .filter((item) => options.active || item.availableWhenInactive)
    .filter((item) => canOpenTenantPage(permissions, item.permissions))
    .filter((item) => !options.availablePages || !statusDependentEntityPages.includes(item.to) || options.availablePages.includes(item.to));
}

export function availableEntityPages(entity: Pick<EntityDetail, 'status' | 'parentStatus'> | undefined, active: boolean): string[] {
  if (!entity) return [];
  const pages = [entityProfilePage];
  if (active) pages.push(entityEntitiesPage);
  if (entity.status === 'active' && entity.parentStatus === 'active') pages.push(entityPrivacyPage, entityBrandingPage, entitySecurityTxtPage);
  if (entity.status !== 'archived' && entity.parentStatus === 'active') pages.push(entityLifecyclePage);
  return pages;
}

export function searchAdministrationDestinations(
  destinations: readonly AdministrationDestination[],
  query: string
): AdministrationDestination[] {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...destinations];
  return destinations.filter((item) => {
    const haystack = [item.label, item.description, ...item.keywords].join(' ').toLocaleLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

export function firstTenantAdministrationPage(permissions: readonly string[]): string | undefined {
  return tenantAdministrationNavigation.find(
    (item) => !isEntityAdministrationPage(item.to) && canOpenTenantPage(permissions, item.permissions)
  )?.to;
}

export function tenantAdministrationLanding(
  entity: Pick<TenantMembership, 'status' | 'permissions'>
): string | undefined {
  if (entity.status !== 'active') return undefined;
  return hasTenantPermission(entity.permissions, 'tenant.settings.read')
    ? entityOverviewPage
    : firstTenantAdministrationPage(entity.permissions);
}
